import styled from '@emotion/styled';
import * as React from 'react';
import { PricingPlan } from './pricingPlans';
import { StartBuildingLink } from '../../widgets/layout/header/SharedButtons';
import { Link } from 'react-router';
import { ExternalLinks } from '../../RoutesWidget';
import { TagBadgeWidget } from '../../widgets/TagBadgeWidget';

export interface PricingTileProps {
  plan: PricingPlan;
  planIndex: number;
}

export const PricingTile: React.FC<PricingTileProps> = (props) => {
  const { plan, planIndex } = props;

  const priceStr: string = Array.isArray(plan.pricePerAppUserPerMonth)
    ? `$${plan.pricePerAppUserPerMonth[0]} to $${plan.pricePerAppUserPerMonth[1]}`
    : `$${plan.pricePerAppUserPerMonth}`;

  return (
    <S.Tile index={planIndex}>
      <S.TileHeading>
        <S.PlanName index={planIndex}>{plan.name}</S.PlanName>
        <S.Description>{plan.description}</S.Description>
      </S.TileHeading>
      <S.PricingDetails>
        <S.Price>{priceStr}</S.Price>
        <S.PriceDescription>USD / App user / Month</S.PriceDescription>
        <S.PriceFootnote>{plan.priceFootnote}</S.PriceFootnote>
        {plan.startBuilding ? <S.StartLink /> : <S.ContactUs to={ExternalLinks.CONTACT_US}>Contact us</S.ContactUs>}
      </S.PricingDetails>
    </S.Tile>
  );
};

export const CustomPricingTile: React.FC = () => (
  <S.Tile index={3}>
    <S.TileHeading>
      <S.PlanNameRow>
        <S.PlanName index={3}>Custom pricing</S.PlanName>
        <TagBadgeWidget>New</TagBadgeWidget>
      </S.PlanNameRow>
      <S.Description>Tailored to what you need.</S.Description>
    </S.TileHeading>
    <S.PricingDetails>
      <S.CustomPrice>Let&apos;s talk</S.CustomPrice>
      <S.PriceDescription>Flexible pricing for your requirements</S.PriceDescription>
      <S.PriceFootnote />
      <S.ContactUs to={ExternalLinks.CONTACT_US}>Contact us</S.ContactUs>
    </S.PricingDetails>
  </S.Tile>
);

namespace S {
  export const Tile = styled.div<{ index: number }>`
    border: 1px solid ${(p) => p.theme.text.listBorder};
    border-top: 3px solid ${(p) => p.theme.pricing[`background${p.index}`]};
    border-radius: 18px;
    background: ${(p) => p.theme.card.background};
    overflow: hidden;
    width: min(100%, 260px);
    display: flex;
    flex-direction: column;
    box-shadow: ${(p) => p.theme.surface.shadow};
    transition:
      transform 160ms ease,
      border-color 160ms ease;

    &:hover {
      transform: translateY(-3px);
      border-color: ${(p) => p.theme.pricing[`background${p.index}`]};
    }
  `;

  export const TileHeading = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 9px;
    padding: 24px 24px 0;
  `;

  export const PlanNameRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 9px;
  `;

  export const PlanName = styled.div<{ index: number }>`
    color: ${(p) => p.theme.pricing[`checkIcon${p.index}`]};
    font-size: ${(p) => (p.index === 3 ? 20 : 23)}px;
    font-weight: 700;
    white-space: nowrap;
  `;

  export const PricingDetails = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 22px 24px 24px;
    gap: 7px;
  `;

  export const Description = styled.div`
    min-height: 72px;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    line-height: 1.6;
  `;

  export const ContactUs = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    padding: 0 16px;
    border: 1px solid ${(p) => p.theme.text.listBorder};
    border-radius: 9px;
    color: ${(p) => p.theme.text.heading};
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      border-color: ${(p) => p.theme.text.special};
    }
  `;

  export const StartLink = styled(StartBuildingLink)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    padding: 0 16px;
    border-radius: 9px;
    background: ${(p) => p.theme.text.special};
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    &:hover {
      filter: brightness(1.08);
      text-decoration: none;
    }
  `;

  export const Price = styled.div`
    height: 40px;
    margin-top: 6px;
    font-size: 36px;
    font-weight: 500;
    line-height: 1.1;
    color: ${(p) => p.theme.text.heading};
  `;

  export const CustomPrice = styled.div`
    height: 40px;
    display: flex;
    align-items: center;
    margin-top: 6px;
    color: ${(p) => p.theme.text.heading};
    font-size: 36px;
    font-weight: 500;
    line-height: 1.1;
  `;

  export const PriceDescription = styled.div`
    min-height: 38px;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 13px;
    line-height: 1.45;
  `;

  export const PriceFootnote = styled.div`
    min-height: 38px;
    margin-bottom: 12px;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
  `;
}
